import { I18n } from '@calendly/i18n';
import { getTimezoneName } from '@calendly/timezone-tools';
import { Button } from '@calendly/ui/components/button';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, CalendarAddIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { sortBy, groupBy, isEmpty, mapValues, fromPairs } from 'lodash-es';
import moment from 'moment-timezone';
import { useContext, useMemo, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { dateNotationSelector, timeNotationSelector, timezoneSelector } from 'selectors/current_user';
import { formatDuration } from 'services/event_types';
import { formatTime, userDateFormatCheckYear } from 'services/time_format';
import { MeetingDetailDependenciesContext } from '../../../meeting_detail_context';
import { WidgetContainer } from '../widget_container';
import { BookingModal } from './booking_modal';
import { useInviteeVoteTooltip } from './invitee_vote_tooltip';
import { ThumbsUpIcon } from './thumbs_up_icon';
const formatSpotTime = (spotTime, timezone, timeNotation) => {
  const time = moment(spotTime).tz(timezone);
  return formatTime(time, timeNotation);
};
const getSubtitle = ({
  timezone,
  event
}) => <span>
    {getTimezoneName(timezone)}
    <br />
    {formatDuration(event.duration)}
  </span>;
const spotDisplayContainerIsPastCss = "s1kpygyx";
const spotDisplayContainerHasVotesCss = "s127y845";
const spotDisplayContainerCss = "s1dljbv";
const SpotDisplayContainer = forwardRef((props, ref) => {
  const {
    isPast,
    hasVotes,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(spotDisplayContainerCss, {
    [spotDisplayContainerIsPastCss]: isPast,
    [spotDisplayContainerHasVotesCss]: hasVotes
  }, props.className)} />;
});
const spotInfoContainerCss = "s1vmov94";
const SpotInfoContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(spotInfoContainerCss, props.className)} />;
});
const votesContainerHasVotesCss = "v1q4mpzk";
const votesContainerCss = "v10x38xc";
const VotesContainer = forwardRef((props, ref) => {
  const {
    hasVotes,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(votesContainerCss, {
    [votesContainerHasVotesCss]: hasVotes
  }, props.className)} />;
});
const timeAndWarningsContainerCss = "t8em82";
const TimeAndWarningsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(timeAndWarningsContainerCss, props.className)} />;
});
const statusMessageCss = "s1iumg9e";
const StatusMessage = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(statusMessageCss, props.className)} />;
});
const thumbsUpCss = "t1tsp7ca";
const ThumbsUp = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(thumbsUpCss, props.className)}>
      <ThumbsUpIcon />
    </Component>;
});
const SpotDisplay = ({
  spot,
  timezone,
  setSelectedSpot
}) => {
  const timeNotation = useSelector(timeNotationSelector);
  const isPast = moment(spot.startTime).isBefore(moment());
  const hasVotes = spot.invitees.length > 0;
  const [InviteeVoteTooltip, tooltipHostProps] = useInviteeVoteTooltip({
    invitees: spot.invitees
  });
  return <SpotDisplayContainer isPast={isPast} hasVotes={hasVotes}>
      <SpotInfoContainer>
        <VotesContainer hasVotes={hasVotes} {...tooltipHostProps}>
          <ThumbsUp />
          {spot.invitees.length}
        </VotesContainer>
        <TimeAndWarningsContainer>
          {formatSpotTime(spot.startTime, timezone, timeNotation)}
        </TimeAndWarningsContainer>
      </SpotInfoContainer>
      <div>
        {isPast && <StatusMessage>{I18n.t('publisher.meetings.best_times.time_is_past')}</StatusMessage>}
        {!isPast && !hasVotes && <StatusMessage>{I18n.t('publisher.meetings.best_times.no_votes_yet')}</StatusMessage>}
        {!isPast && hasVotes && <Button decoration="secondary-outline" size="small" onClick={() => setSelectedSpot(spot)}>
            <CalendarAddIcon />
            {I18n.t('publisher.meetings.best_times.book_meeting_time')}
          </Button>}
      </div>
      <InviteeVoteTooltip />
    </SpotDisplayContainer>;
};
const dayDisplayCss = "dvk08qg";
const DayDisplay = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dayDisplayCss, props.className)} />;
});
const bodyCss = "b1qfju7z";
const Body = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyCss, props.className)} />;
});
const ChangeViewButton = ({
  viewAll,
  toggleViewAll,
  hasTopSpots
}) => <div>
    <MinimalButton onClick={toggleViewAll}>
      {!viewAll && <>
            {I18n.t('publisher.meetings.best_times.view_all_times')}
            <ArrowChevronDownMiniIcon />
          </>}
      {viewAll && <>
            {hasTopSpots ? I18n.t('publisher.meetings.best_times.show_only_top_results') : I18n.t('publisher.meetings.best_times.hide_all_times')}
            <ArrowChevronUpMiniIcon />
          </>}
    </MinimalButton>
  </div>;
export const BestTimesWidget = ({
  event
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies?.useGetPollResultsQuery) {
    throw new Error('BestTimesWidget must be wrapped in a MeetingDetailDependenciesProvider with useGetPollResultsQuery');
  }
  const {
    data,
    isSuccess
  } = dependencies.useGetPollResultsQuery(event.eventTypeId);
  const [viewAll, setViewAll] = useState(false);
  const timezone = useSelector(timezoneSelector);
  const dateNotation = useSelector(dateNotationSelector);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const sortedSpots = useMemo(() => {
    if (!isSuccess) return [];
    return sortBy(data.spots, spot => -spot.invitees.length);
  }, [data, isSuccess]);
  const topSpots = useMemo(() => {
    if (isEmpty(sortedSpots)) return [];
    const top = sortedSpots.filter(spot => spot.invitees.length).slice(0, 5);
    return groupBy(top, spot => moment(spot.startTime).tz(timezone).format('YYYY-MM-DD'));
  }, [sortedSpots, timezone]);
  const allSpots = useMemo(() => {
    const grouped = groupBy(sortedSpots, spot => moment(spot.startTime).tz(timezone).format('YYYY-MM-DD'));
    const sortedGroups = mapValues(grouped, spots => sortBy(spots, 'startTime'));
    return fromPairs(sortBy(Object.entries(sortedGroups), ([date]) => date));
  }, [sortedSpots, timezone]);
  const spotsToDisplay = viewAll ? allSpots : topSpots;
  if (!isSuccess) return null;
  return <WidgetContainer title={I18n.t('publisher.meetings.best_times.title')} subtitle={getSubtitle({
    timezone,
    event
  })}>
      <Body>
        {!viewAll && isEmpty(spotsToDisplay) && <StatusMessage>{I18n.t('publisher.meetings.best_times.no_votes_yet')}</StatusMessage>}
        {Object.keys(spotsToDisplay).map(date => <DayDisplay key={date}>
            <BodyText weight="bold">
              {userDateFormatCheckYear(moment(date).tz(timezone), dateNotation)}
            </BodyText>
            {spotsToDisplay[date].map(spot => <SpotDisplay key={spot.startTime} event={event} spot={spot} timezone={timezone} setSelectedSpot={setSelectedSpot} />)}
          </DayDisplay>)}
        <ChangeViewButton viewAll={viewAll} toggleViewAll={() => setViewAll(!viewAll)} hasTopSpots={!isEmpty(topSpots)} />
        <BookingModal event={event} selectedSpot={selectedSpot} visible={!!selectedSpot} onClose={() => setSelectedSpot(null)} />
      </Body>
    </WidgetContainer>;
};

require("./best_times_widget.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./best_times_widget.js");